// Profile picture upload via the Firebase Storage SDK.
//
// Required Storage Rules for this path:
//
//   service firebase.storage {
//     match /b/{bucket}/o {
//       match /profile-images/{userId}/{file=**} {
//         allow read;
//         allow write: if request.auth != null && request.auth.uid == userId
//                      && request.resource.size < 1024 * 1024
//                      && request.resource.contentType.matches('image/.*');
//       }
//     }
//   }
//
// We pre-process the image client-side: crop to the user's framing, resize
// to 512x512, and re-encode as JPEG at ~0.85 quality. Final blobs are well
// under 100 KB, so the bucket never holds the original full-resolution photo.
import { useMutation } from "@tanstack/react-query";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirebaseStorage, getCurrentFirebaseUser } from "../firebase";

function randomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export async function uploadProfileImage(blob: Blob): Promise<string> {
  const user = getCurrentFirebaseUser();
  if (!user) {
    throw new Error("You need to be signed in to upload an image");
  }
  const path = `profile-images/${user.uid}/${randomId()}.jpg`;
  const fileRef = ref(getFirebaseStorage(), path);
  await uploadBytes(fileRef, blob, { contentType: "image/jpeg" });
  return getDownloadURL(fileRef);
}

export function useProfileImageUpload() {
  return useMutation({
    mutationFn: (blob: Blob) => uploadProfileImage(blob),
  });
}
